import { DATA_STYLE,HEADER_STYLE, SUBHEADER_STYLE } from "../excelReportStyles";
import {
  addBordersToSheet,
  ANNUAL_BENEFIT_KEYS,
  formatPlantTrainName,
} from "../excelReportUtils";

export const createBenchmarkOptimizationSheet = (
  sheet,
  trainsData,
  t,
  currencySymbol = "$",
  volumeUnit = "m3",
) => {
  const hasAnnualBenefits = trainsData.some(
    (train) =>
      train.ca_predictions &&
      ANNUAL_BENEFIT_KEYS.some(
        (key) =>
          train.ca_predictions[key] !== undefined &&
          train.ca_predictions[key] !== null,
      ),
  );

  const topMetrics = [
    {
      label: t("benchMark.headers.objective"),
      key: null,
      isDynamic: true,
      dynamicKey: "optimization_target",
    },
    {
      label: t("benchMark.headers.forecastWindow"),
      key: "forcast_window",
    },
  ];

  const benefitsMetrics = [
    {
      label: t("benchMark.headers.energySavingsPercent"),
      key: "energy_saving_percent",
      annualKey: "annual_energy_saving_percent",
      format: "percentage",
      unit: "%",
    },
    {
      label: t("benchMark.headers.energySavings"),
      key: "energy_saving_dollars",
      annualKey: "annual_energy_saving_dollars",
      format: "currency",
      unit: currencySymbol,
    },
    {
      label: t("benchMark.headers.chemicalSavingsPercent"),
      key: "chemical_saving_percent",
      annualKey: "annual_chemical_saving_percent",
      format: "percentage",
      unit: "%",
    },
    {
      label: t("benchMark.headers.chemicalSavings"),
      key: "chemical_saving_dollars",
      annualKey: "annual_chemical_saving_dollars",
      format: "currency",
      unit: currencySymbol,
    },
    {
      label: t("benchMark.headers.downtimeSavingsPercent"),
      key: "downtime_saving_percent",
      annualKey: "annual_downtime_saving_percent",
      format: "percentage",
      unit: "%",
    },
    {
      label: t("benchMark.headers.downtimeSavings"),
      key: "downtime_saving_dollars",
      annualKey: "annual_downtime_saving_dollars",
      format: "currency",
      unit: currencySymbol,
    },
    {
      label: t("benchMark.headers.downtimeSavingsDays"),
      key: "downtime_saving_days",
      annualKey: "annual_downtime_saving_days",
      format: "number",
      unit: "days",
    },
    {
      label: t("benchMark.headers.increasedProductionPercent"),
      key: "increased_production_percent",
      annualKey: "annual_increased_production_percent",
      format: "percentage",
      unit: "%",
    },
    {
      label: t("benchMark.headers.energySavings"),
      key: "increased_production_dollars",
      annualKey: "annual_increased_production_dollars",
      format: "currency",
      unit: currencySymbol,
    },
    {
      label: t("benchMark.headers.increasedProduction"),
      key: "increased_production",
      annualKey: "annual_increased_production",
      format: "volume",
      unit: volumeUnit,
    },
    {
      label: t("benchMark.headers.total"),
      key: "total_financial_benefits",
      annualKey: "annual_total_financial_benefits",
      format: "currency",
      unit: currencySymbol,
    },
  ];

  const headerRow1 = sheet.getRow(1);
  headerRow1.getCell(1).value = t("benchMark.headers.metric");
  Object.assign(headerRow1.getCell(1), HEADER_STYLE);

  let colIndex = 2;
  trainsData.forEach((train) => {
    const benefitsCell = headerRow1.getCell(colIndex);
    benefitsCell.value = formatPlantTrainName(
      train.plant_name,
      train.plant_id,
      train.train_num,
    );
    Object.assign(benefitsCell, HEADER_STYLE);

    if (hasAnnualBenefits) {
      sheet.mergeCells(1, colIndex, 1, colIndex + 1);
      colIndex += 2;
    } else {
      colIndex += 1;
    }
  });
  headerRow1.commit();

  if (hasAnnualBenefits) {
    const headerRow2 = sheet.getRow(2);
    headerRow2.getCell(1).value = "";

    colIndex = 2;
    trainsData.forEach(() => {
      const totalCell = headerRow2.getCell(colIndex++);
      totalCell.value = t("benchMark.headers.financialBenefits");
      Object.assign(totalCell, SUBHEADER_STYLE);

      const annualCell = headerRow2.getCell(colIndex++);
      annualCell.value = t("benchMark.headers.annualBenefits");
      Object.assign(annualCell, SUBHEADER_STYLE);
    });
    headerRow2.commit();
  }

  topMetrics.forEach((metric, metricIndex) => {
    const rowNum = (hasAnnualBenefits ? 3 : 2) + metricIndex;
    const row = sheet.getRow(rowNum);

    row.getCell(1).value = metric.label;
    Object.assign(row.getCell(1), DATA_STYLE);

    const getValue = (train) => {
      if (metric.isDynamic && metric.dynamicKey) {
        return train[metric.dynamicKey] || "-";
      }
      if (train.ca_predictions?.[metric.key]) {
        const value = train.ca_predictions[metric.key];
        return typeof value === "number" ? value.toFixed(2) : value;
      }
      return "-";
    };

    let colIdx = 2;
    trainsData.forEach((train) => {
      const value = getValue(train);

      const totalCell = row.getCell(colIdx++);
      totalCell.value = value;
      Object.assign(totalCell, DATA_STYLE);

      if (hasAnnualBenefits) {
        const annualCell = row.getCell(colIdx++);
        annualCell.value = value;
        Object.assign(annualCell, DATA_STYLE);
      }
    });

    row.commit();
  });

  const formatMetricValue = (value, metric) => {
    if (value == null) return "-";
    const formatted = value.toFixed(2);
    if (metric.format === "currency") return `${metric.unit} ${formatted}`;
    if (metric.format === "volume") return `${formatted} ${metric.unit}`;
    return formatted;
  };

  benefitsMetrics.forEach((metric, metricIndex) => {
    const rowNum = (hasAnnualBenefits ? 5 : 4) + metricIndex;
    const row = sheet.getRow(rowNum);

    row.getCell(1).value = metric.label;
    Object.assign(row.getCell(1), DATA_STYLE);

    let colIdx = 2;
    trainsData.forEach((train) => {
      const totalCell = row.getCell(colIdx++);
      totalCell.value = formatMetricValue(train.ca_predictions?.[metric.key], metric);
      Object.assign(totalCell, DATA_STYLE);

      if (hasAnnualBenefits) {
        const annualCell = row.getCell(colIdx++);
        annualCell.value = formatMetricValue(
          train.ca_predictions?.[metric.annualKey],
          metric,
        );
        Object.assign(annualCell, DATA_STYLE);
      }
    });

    row.commit();
  });

  sheet.getColumn(1).width = 35;
  let colNum = 2;
  trainsData.forEach(() => {
    sheet.getColumn(colNum++).width = 15;
    if (hasAnnualBenefits) {
      sheet.getColumn(colNum++).width = 15;
    }
  });

  sheet.getRow(1).height = 20;
  if (hasAnnualBenefits) {
    sheet.getRow(2).height = 20;
  }
  sheet.getRow(3).height = 20;
  sheet.getRow(4).height = 20;

  addBordersToSheet(sheet);
};
